/**
 * Contextual D&D 5e size picker.
 *
 * Appears at the bottom of the VTT page whenever exactly one image
 * element is selected. Clicking a size re-stamps the element's width
 * and height to the cell footprint for that category — the renderer
 * fits the natural image aspect inside the box, so Gargantuan dragons
 * and Tiny familiars all stay visually consistent with the grid.
 *
 * Uses useSyncExternalStore against the scene store so it shows/hides
 * without forcing every selection change through React state.
 */
import { useSyncExternalStore } from "react";
import { sceneStore } from "../store/sceneStore";
import {
  SIZE_CATEGORIES,
  matchSize,
  sizeToMapUnits,
  type SizeCategory,
} from "../render/sizeCategories";
import "./SizePicker.css";

export default function SizePicker() {
  // Subscribe via the per-notify counter — getState() returns a stable
  // reference, so without this React would not re-render when the
  // selection changes.
  useSyncExternalStore(
    (cb) => sceneStore.subscribe(cb),
    () => sceneStore.getNotifyVersion(),
    () => sceneStore.getNotifyVersion(),
  );
  const state = sceneStore.getState();

  const ids = [...state.selectedIds];
  if (ids.length !== 1) return null;
  const el = state.elements[ids[0]];
  if (!el || el.type !== "image" || el.isDeleted) return null;

  const current: SizeCategory = matchSize(el.width, el.height);

  const apply = (size: SizeCategory) => {
    const side = sizeToMapUnits(size);
    // Keep the element centred on its current centre so resizing doesn't
    // slide the token out from under the player pointer.
    const cx = el.x + el.width / 2;
    const cy = el.y + el.height / 2;
    const next = sceneStore.bumpLocal({
      ...el,
      x: cx - side / 2,
      y: cy - side / 2,
      width: side,
      height: side,
    });
    sceneStore.upsertLocal(next);
  };

  return (
    <div className="size-picker" role="toolbar" aria-label="Image size">
      <span className="size-picker__label">Size</span>
      {SIZE_CATEGORIES.map((s) => (
        <button
          key={s.key}
          type="button"
          className={`size-picker__btn${current === s.key ? " is-active" : ""}`}
          onClick={() => apply(s.key)}
          title={`${s.label} (${s.cells * 5} ft)`}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}
